import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_CURRENCY, DEFAULT_DOCUMENT_AI_MODEL, documentAiModels } from '@almanac/shared';
import { useCurrentUser } from '../lib/me.js';
import { offlineEnabled } from '../lib/offlineFiles.js';
import { usePeople } from '../lib/people.js';
import { AccountSection } from './AccountSection.js';
import { CurrencySection } from './CurrencySection.js';
import { DocumentAiSection } from './DocumentAiSection.js';
import { FileNamesSection } from './FileNamesSection.js';
import { OfflineVaultCard } from './OfflineVaultCard.js';
import { HomeLayoutSection } from './HomeLayoutSection.js';
import { ReminderToggle } from './ReminderToggle.js';
import { ThemeSection } from './ThemeSection.js';
import { ThisIsMeSection } from './ThisIsMeSection.js';

/**
 * App-wide settings, opened from the gear icon in any page header.
 *
 * A grouped menu rather than one flat page: four groups (Account,
 * Appearance, Documents & AI, Reminders), each row showing its current
 * value at a glance and opening its own panel. The existing section
 * components render unchanged inside their panels — the menu is just
 * navigation on top.
 *
 * Borrows the visual shell from `EditDrawer` — same scrim + bottom-
 * sheet-on-mobile, side-panel-on-desktop pattern — but skips the
 * drag-to-dismiss complexity. Escape (or the header ‹) backs out of a
 * panel first, then closes.
 */

type Panel = 'menu' | 'account' | 'appearance' | 'documents' | 'offline' | 'budget' | 'reminders';

const PANEL_TITLES: Record<Exclude<Panel, 'menu'>, string> = {
  account: 'Account',
  appearance: 'Appearance',
  documents: 'Documents & AI',
  offline: 'Offline',
  budget: 'Budget',
  reminders: 'Reminders',
};

export function SettingsSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [panel, setPanel] = useState<Panel>('menu');
  const me = useCurrentUser();
  const people = usePeople();

  // Every open starts at the menu.
  useEffect(() => {
    if (open) setPanel('menu');
  }, [open]);

  // Escape backs out one level (panel → menu → closed).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      setPanel((current) => {
        if (current === 'menu') {
          onClose();
          return current;
        }
        return 'menu';
      });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const mePerson = me.data?.personId
    ? people.data?.find((p) => p.id === me.data.personId)
    : undefined;
  const modelId = me.data?.documentAiModel ?? DEFAULT_DOCUMENT_AI_MODEL;
  const modelLabel = documentAiModels.find((m) => m.id === modelId)?.label ?? 'Default';

  const accountValue = [me.data?.email, mePerson ? `You are ${mePerson.displayName}` : null]
    .filter(Boolean)
    .join(' · ');

  return createPortal(
    <>
      <div
        className="drawer-scrim"
        onClick={onClose}
        role="presentation"
      />
      <aside
        className="drawer settings-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
      >
        <div className="drawer__header">
          <h2 className="drawer__title">
            {panel === 'menu' ? (
              'Settings'
            ) : (
              <>
                <button
                  type="button"
                  className="settings-back"
                  onClick={() => setPanel('menu')}
                  aria-label="Back to settings"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M15 18l-6-6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <span className="settings-crumb">Settings ›</span> {PANEL_TITLES[panel]}
              </>
            )}
          </h2>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={onClose}
            aria-label="Close settings"
          >
            Close
          </button>
        </div>
        <div className="drawer__body">
          {panel === 'menu' && (
            <>
              <div className="settings-menu">
                <MenuRow
                  icon={<AccountIcon />}
                  label="Account"
                  value={accountValue || 'Signed in'}
                  onOpen={() => setPanel('account')}
                />
              </div>
              <div className="settings-menu">
                <MenuRow
                  icon={<AppearanceIcon />}
                  label="Appearance"
                  value="Theme · Home layout"
                  onOpen={() => setPanel('appearance')}
                />
                <MenuRow
                  icon={<DocumentsIcon />}
                  label="Documents & AI"
                  value={`Read with AI: ${modelLabel}`}
                  onOpen={() => setPanel('documents')}
                />
                <MenuRow
                  icon={<OfflineIcon />}
                  label="Offline"
                  value={offlineEnabled() ? 'Keeping the Vault on this device' : 'Off'}
                  onOpen={() => setPanel('offline')}
                />
                <MenuRow
                  icon={<BudgetIcon />}
                  label="Budget"
                  value={`Currency: ${me.data?.currency ?? DEFAULT_CURRENCY}`}
                  onOpen={() => setPanel('budget')}
                />
                <MenuRow
                  icon={<RemindersIcon />}
                  label="Reminders"
                  value="This device"
                  onOpen={() => setPanel('reminders')}
                />
              </div>
            </>
          )}

          {panel === 'account' && (
            <>
              <section className="settings-section">
                <AccountSection />
              </section>
              <section className="settings-section">
                <ThisIsMeSection />
              </section>
            </>
          )}

          {panel === 'appearance' && (
            <>
              <section className="settings-section">
                <ThemeSection />
              </section>
              <section className="settings-section">
                <HomeLayoutSection />
              </section>
            </>
          )}

          {panel === 'documents' && (
            <>
              <section className="settings-section">
                <DocumentAiSection />
              </section>
              <section className="settings-section">
                <FileNamesSection />
              </section>
            </>
          )}

          {panel === 'offline' && (
            <section className="settings-section">
              <OfflineVaultCard />
            </section>
          )}

          {panel === 'budget' && (
            <section className="settings-section">
              <CurrencySection />
            </section>
          )}

          {panel === 'reminders' && (
            <section className="settings-section">
              <ReminderToggle />
            </section>
          )}
        </div>
      </aside>
    </>,
    document.body,
  );
}

function MenuRow({
  icon,
  label,
  value,
  onOpen,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onOpen: () => void;
}) {
  return (
    <button type="button" className="settings-menu__row" onClick={onOpen}>
      <span className="settings-menu__icon" aria-hidden>
        {icon}
      </span>
      <span className="settings-menu__mid">
        <span className="settings-menu__label">{label}</span>
        <span className="settings-menu__value">{value}</span>
      </span>
      <svg
        className="settings-menu__chev"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
      >
        <path
          d="M9 6l6 6-6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function AccountIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AppearanceIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function DocumentsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 3v5h5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OfflineIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 4v10M8 10l4 4 4-4M5 19h14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BudgetIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="7"
        width="18"
        height="10"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function RemindersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.3 21a2 2 0 0 0 3.4 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
